import { type Common } from '../common.js';
import {
  type CloneResponse,
  type CloneValues,
  type CreateValues,
  type FindAllOptions,
  PreviewEntityKind,
  type PreviewEntityParams,
  type PreviewMessage,
  type PreviewOptions,
  type Scheme,
  type SchemeFull,
  type SchemeListEntry,
  type SchemeWithConfigs,
  type UpdateValues,
} from '../models/notification/scheme.js';
import { type PaginatedQueryOptions } from '../models/pagination/PaginatedQueryOptions.type.js';
import { type PaginationResponse } from '../models/pagination/PaginationResponse.type.js';

export class API {
  common: Common;
  baseEndpoint: string;

  constructor(common: Common) {
    this.common = common;
    this.baseEndpoint = '/notification-schemes';
  }

  async findAll(options?: FindAllOptions): Promise<Scheme[]> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    return await this.common.getJSON(url);
  }

  async findAllForList(options?: FindAllOptions): Promise<SchemeListEntry[]> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    return await this.common.getJSON(url);
  }

  async findAllPaginated(
    options: PaginatedQueryOptions<FindAllOptions>
  ): Promise<PaginationResponse<SchemeListEntry>> {
    const url = this.common.constructPath(`${this.baseEndpoint}/list`, options);
    return await this.common.getJSON(url);
  }

  async findOneById(id: string): Promise<SchemeFull> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.getJSON(url);
  }

  async preview(id: string, entity: PreviewEntityParams): Promise<PreviewMessage[]> {
    let query: PreviewOptions;
    if (entity.entityKind === PreviewEntityKind.Encounter) {
      query = { encounterId: entity.entityId };
    } else {
      query = { ehrInformationId: entity.entityId };
    }
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/preview`, query);
    return await this.common.getJSON(url);
  }

  async create(values: CreateValues): Promise<SchemeWithConfigs> {
    const url = this.common.constructPath(this.baseEndpoint);
    return await this.common.postJSON(url, values);
  }

  async clone(id: string, values: CloneValues): Promise<CloneResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/clone`);
    return await this.common.postJSON(url, values);
  }

  async update(id: string, values: UpdateValues): Promise<SchemeWithConfigs> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.patchJSON(url, values);
  }

  async remove(id: string): Promise<{ success: true }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.deleteJSON(url);
  }
}
